/**
 * Goal utility functions — progress calculation, colors, milestones.
 */

import { z } from 'zod'
import { getDayOfYear, getDaysInYear, isLeapYear } from 'date-fns'
import type { Goal } from '@turnifly/shared/types'

// ── Milestone Schema (typed replacement for z.any()) ──

export const MilestoneSchema = z.object({
  month: z.number().min(1).max(12),
  target: z.number(),
  achieved: z.number().default(0),
})

export type Milestone = z.infer<typeof MilestoneSchema>

export const MilestonesArraySchema = z.array(MilestoneSchema).catch([])

// ── Progress Calculation ──

export function calculateProgress(goal: Goal): number {
  const { direction, current_value, target_value, start_value } = goal

  if (target_value === 0) return 0

  let percent: number

  if (direction === 'decrease') {
    const denominator = start_value - target_value
    if (denominator === 0) return 0
    percent = ((start_value - current_value) / denominator) * 100
  } else {
    percent = (current_value / target_value) * 100
  }

  return Math.min(Math.max(Math.round(percent), 0), 100)
}

// ── Progress Color ──

export function getProgressColor(percent: number): string {
  if (percent >= 80) return 'text-success'
  if (percent >= 60) return 'text-streak'
  if (percent >= 40) return 'text-warning'
  return 'text-danger'
}

export function getProgressBarVariant(percent: number): 'success' | 'streak' | 'primary' {
  if (percent >= 80) return 'success'
  if (percent >= 60) return 'streak'
  return 'primary'
}

// ── Year Progress ──

interface YearProgress {
  daysPassed: number
  totalDays: number
  percent: number
}

export function getYearProgress(year: number): YearProgress {
  const now = new Date()
  const currentYear = now.getFullYear()

  if (year < currentYear) {
    const total = isLeapYear(new Date(year, 0, 1)) ? 366 : 365
    return { daysPassed: total, totalDays: total, percent: 100 }
  }

  if (year > currentYear) {
    const total = isLeapYear(new Date(year, 0, 1)) ? 366 : 365
    return { daysPassed: 0, totalDays: total, percent: 0 }
  }

  const totalDays = getDaysInYear(now)
  const daysPassed = getDayOfYear(now)
  const percent = Math.round((daysPassed / totalDays) * 100)

  return { daysPassed, totalDays, percent }
}

// ── Auto Milestones Generator ──

export function generateAutoMilestones(
  targetValue: number,
  startMonth: number = 1,
): Milestone[] {
  const months = 12 - startMonth + 1
  if (months <= 0) return []

  const perMonth = Math.round(targetValue / months)
  const milestones: Milestone[] = []

  let remaining = targetValue
  for (let m = startMonth; m <= 12; m++) {
    const isLast = m === 12
    const target = isLast ? remaining : perMonth
    milestones.push({ month: m, target, achieved: 0 })
    remaining -= target
  }

  return milestones
}

// ── Parse milestones from JSONB ──

export function parseMilestones(raw: unknown): Milestone[] {
  return MilestonesArraySchema.parse(raw)
}

// ── Status helpers ──

export function isGoalActive(goal: Goal): boolean {
  return goal.status === 'planned' || goal.status === 'in_progress'
}

export function isGoalCompleted(goal: Goal): boolean {
  return goal.status === 'achieved'
}

// ── Deadline helpers ──

export type DeadlineStatus = 'none' | 'safe' | 'warning' | 'overdue'

interface DeadlineInfo {
  status: DeadlineStatus
  daysLeft: number
}

export function getDeadlineInfo(goal: Goal): DeadlineInfo {
  if (!goal.deadline || goal.status === 'achieved' || goal.status === 'archived') {
    return { status: 'none', daysLeft: 0 }
  }

  const now = new Date()
  const deadline = new Date(goal.deadline + 'T23:59:59')
  const diffMs = deadline.getTime() - now.getTime()
  const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (daysLeft < 0) return { status: 'overdue', daysLeft: Math.abs(daysLeft) }
  if (daysLeft <= 7) return { status: 'warning', daysLeft }
  return { status: 'safe', daysLeft }
}
